import React from 'react';
import { Search, RotateCcw } from 'lucide-react';
import {
  Button,
  DatePicker,
  Input,
  VirtualizedMultiSelectCombobox,
} from '@roger/ui-components';
import type { RemittanceTableQuery } from '../../hooks/useRemittanceTableQuery';
import type {
  RemittancePayorOption,
  RemittanceSource,
} from '../../services/api/types.gen';
import { SOURCE_FILTER_OPTIONS } from './remittanceLabels';

export interface RemittanceTableToolbarProps {
  query: RemittanceTableQuery;
  payors: RemittancePayorOption[];
  onReset: () => void;
  /** Rendered right-aligned; the manager passes its Upload 835 button. */
  uploadSlot?: React.ReactNode;
}

export function RemittanceTableToolbar({
  query,
  payors,
  onReset,
  uploadSlot,
}: RemittanceTableToolbarProps) {
  // Local input state keeps typing responsive; the committed search is debounced
  // (~250ms) so each keystroke does not trigger a server round-trip.
  const [searchValue, setSearchValue] = React.useState(query.search);

  React.useEffect(() => {
    setSearchValue(query.search);
  }, [query.search]);

  React.useEffect(() => {
    if (searchValue === query.search) return;
    const handle = setTimeout(() => query.setSearch(searchValue), 250);
    return () => clearTimeout(handle);
  }, [searchValue, query.search, query.setSearch]);

  return (
    <div className='mb-3 grid grid-cols-2 gap-2 md:flex md:flex-wrap md:items-center'>
      <div className='relative col-span-2 md:col-auto'>
        <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
        <Input
          inputSize='lg'
          placeholder='Search…'
          value={searchValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchValue(e.target.value)
          }
          className='w-full md:w-72 pl-9'
        />
      </div>

      {/* No status control here: status is the tab bar above the table, the
          same way the claims tabs own claim status. */}
      <VirtualizedMultiSelectCombobox
        options={payors.map(p => ({ value: p.id, label: p.name }))}
        value={query.payorIds}
        onValueChange={query.setPayorIds}
        placeholder='All Insurances'
        searchPlaceholder='Search insurances…'
        buttonClassName='h-9 w-full md:w-48'
      />
      <VirtualizedMultiSelectCombobox
        options={SOURCE_FILTER_OPTIONS}
        value={query.sources}
        onValueChange={v => query.setSources(v as RemittanceSource[])}
        placeholder='All Sources'
        searchPlaceholder='Search sources…'
        buttonClassName='h-9 w-full md:w-44'
      />

      {/* 'lg' is h-9, matching the comboboxes and the search input on this row. */}
      <DatePicker
        datePickerSize='lg'
        showClearButton
        placeholder='Deposit from'
        date={query.depositDateFrom}
        onDateChange={query.setDepositDateFrom}
        className='w-full md:w-[150px]'
      />
      <DatePicker
        datePickerSize='lg'
        showClearButton
        placeholder='Deposit to'
        date={query.depositDateTo}
        onDateChange={query.setDepositDateTo}
        className='w-full md:w-[150px]'
      />

      <Button
        variant='ghost'
        size='sm'
        onClick={() => {
          // Clear the local input so a pending debounced search cannot re-apply
          // after reset (searchValue === '' === query.search => no reschedule).
          setSearchValue('');
          onReset();
        }}
        className='col-span-2 justify-self-start text-muted-foreground hover:text-foreground md:col-auto'
      >
        <RotateCcw className='mr-1 h-3 w-3' />
        Reset
      </Button>

      {uploadSlot && (
        <div
          data-testid='remittance-toolbar-actions'
          className='col-span-2 flex w-full items-center justify-start gap-2 md:col-auto min-[1680px]:ml-auto min-[1680px]:w-auto'
        >
          {uploadSlot}
        </div>
      )}
    </div>
  );
}
